import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Bell, Heart, Star, BookOpen, MessageCircle, UserPlus, Mail, AtSign, Sparkles, BadgeCheck, Briefcase, FolderInput } from "lucide-react";

const ICONS = {
  follow: UserPlus,
  novelle_like: Heart,
  novelle_rate: Star,
  guestbook: BookOpen,
  thread_reply: MessageCircle,
  thread_moved: FolderInput,
  media_comment: MessageCircle,
  image_like: Heart,
  dm: Mail,
  mention: AtSign,
  mention_accepted: AtSign,
  wheel_winner: Sparkles,
  // Admin-varsler
  creator_application: BadgeCheck,
  coaching_inquiry: Briefcase,
};

function targetLink(n) {
  const actorUsername = n.actor?.username && n.actor.username !== "ukjent" ? n.actor.username : null;
  const fromUsername = n.meta?.from_username || actorUsername;

  // Admin-varsler (creator-søknader, coaching-forespørsler m.fl.) lagrer
  // måldestinasjonen direkte i meta.link. Bruk den hvis satt.
  if (n.meta?.link) return n.meta.link;

  // Ekstern samarbeid-henvendelse (fallback for gamle varsler uten meta.link)
  if (n.kind === "collab_external_message" || n.target_type === "external_collab_message") {
    return n.target_id ? `/skaper/samarbeid/henvendelse/${n.target_id}` : "/skaper/samarbeid?tab=external-inbox";
  }

  // Venneforespørsler → profilen til avsender (der man kan akseptere/avvise)
  if (n.kind === "friend_request" || n.target_type === "friendship") {
    return fromUsername ? `/profile/${fromUsername}` : "/innstillinger/venner";
  }

  // Gruppe-varsler – fallback til /grupper/{target_id} hvis meta.link mangler
  if (
    n.kind === "group_join_request" ||
    n.kind === "group_invite" ||
    n.kind === "group_join_approved" ||
    n.kind === "group_join_rejected" ||
    (n.target_type === "group" && n.target_id)
  ) {
    return n.target_id ? `/grupper/${n.target_id}` : "/grupper";
  }

  if (n.kind === "mention") return "/mentions";
  if (n.kind === "wheel_winner") {
    // Tre varianter:
    //  - Vinneren får varselet (meta.is_winner_notif=true) → ruter til vinst-siden
    //  - Skaper får varselet om anonym vinner → ruter til vinst-siden (full info der)
    //  - Skaper får varselet om innlogget vinner → åpne DM-tråd med vinneren
    if (n.meta?.is_winner_notif && n.meta?.spin_id) {
      return `/lykkehjul/vinst/${n.meta.spin_id}`;
    }
    if (n.meta?.is_anonymous && n.meta?.spin_id) {
      return `/lykkehjul/vinst/${n.meta.spin_id}`;
    }
    const winner = actorUsername || n.meta?.winner_username;
    if (winner) {
      return `/meldinger?context=creator&til=${winner}&via=creator`;
    }
    // Siste fallback: hvis vi har spin_id, vis vinst-siden i stedet for tom innboks
    if (n.meta?.spin_id) {
      return `/lykkehjul/vinst/${n.meta.spin_id}`;
    }
    return "/meldinger?context=creator";
  }
  if (n.kind === "dm" && n.meta?.via === "creator") {
    // DM via skapersiden – åpne i skaper-innboksen, evt. direkte den samtalen
    return actorUsername
      ? `/meldinger?context=creator&til=${actorUsername}&via=creator`
      : "/meldinger?context=creator";
  }
  // Vanlig DM-varsel – åpne samtalen direkte med avsender
  if (n.kind === "dm" || n.target_type === "conversation" || n.target_type === "message") {
    return actorUsername ? `/meldinger?til=${actorUsername}` : "/meldinger";
  }
  if (n.target_type === "novelle") return `/noveller/${n.target_id}`;
  if (n.target_type === "thread") return `/thread/${n.target_id}`;
  if (n.target_type === "image") return `/bilder?image=${n.target_id}`;
  if (n.target_type === "video") return `/bilder?image=${n.target_id}`;
  if (n.target_type === "album") return `/album/${n.target_id}`;
  if (n.target_type === "user") return actorUsername ? `/profile/${actorUsername}` : "/";
  if (n.target_type === "contest") return `/konkurranser/${n.target_id}`;

  // Generisk fallback: hvis vi har en avsender, gå til profilen deres
  if (actorUsername) return `/profile/${actorUsername}`;
  return "/";
}

export default function Varsler() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/notifications?limit=50")
      .then((r) => setNotes(r.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    if (!user || user === false) return;
    load();
  }, [user]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setNotes((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
  };

  if (user === false || user === null) {
    return (
      <Layout>
        <div className="font-fraunces italic text-[var(--cream-soft)]">
          <Link to="/login" className="text-[var(--magenta)]">{t("auth.log_in_btn")}</Link>{" "}
          {t("notifications.login_required")}
        </div>
      </Layout>
    );
  }

  const unread = notes.filter((n) => !n.read_at).length;

  return (
    <Layout withSidebar={false}>
      <div className="flex items-end justify-between mb-10 gap-4">
        <div>
          <div className="font-meta mb-3">{t("notifications.section_label")}</div>
          <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight">{t("notifications.title")}.</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/mentions" className="btn-ghost text-xs" data-testid="varsler-mentions-link">
            {t("notifications.mentions")}
          </Link>
          <Link to="/innstillinger" className="btn-ghost text-xs" data-testid="varsler-prefs-link">
            {t("notifications.settings_link")}
          </Link>
          {unread > 0 && (
            <button onClick={markAll} className="btn-ghost text-xs" data-testid="mark-all-read">
              {t("notifications.all_read")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>
      ) : notes.length === 0 ? (
        <div className="text-center py-20">
          <Bell className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">
            {t("notifications.empty")}
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 max-w-3xl" data-testid="notifications-list">
          {notes.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <li
                key={n.id}
                className={`py-5 flex items-start gap-4 transition-colors ${
                  !n.read_at ? "bg-[var(--magenta)]/5 -mx-4 px-4" : ""
                }`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                  !n.read_at ? "bg-[var(--magenta)]/20 text-[var(--magenta)]" : "bg-white/5 text-[var(--cream-soft)]"
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <Link
                  to={targetLink(n)}
                  onClick={() => !n.read_at && markRead(n.id)}
                  className="flex-1 min-w-0 group"
                >
                  <div className="font-fraunces text-[var(--cream)] text-base leading-snug group-hover:text-[var(--magenta)] transition-colors">
                    {n.summary}
                  </div>
                  <div className="font-meta mt-1">{formatRelative(n.created_at)}</div>
                </Link>
                {!n.read_at && (
                  <button
                    onClick={() => markRead(n.id)}
                    className="font-meta text-[10px] text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                  >
                    {t("notifications.mark_read")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Layout>
  );
}
